import React, { useEffect, useRef } from 'react';
import { Card, Descriptions, Tag, Space, Typography, Row, Col } from 'antd';
import styles from './DeviceCurrentStatus.module.css';
import { 
  WifiOutlined, 
  ThunderboltOutlined, 
  EnvironmentOutlined,
  ClockCircleOutlined,
  SignalFilled 
} from '@ant-design/icons';

const { Text } = Typography;

const renderMapPlaceholder = (container: HTMLDivElement, message: string) => {
  const placeholder = document.createElement('div');
  placeholder.style.width = '100%';
  placeholder.style.height = '100%';
  placeholder.style.background = '#f0f0f0';
  placeholder.style.display = 'flex';
  placeholder.style.alignItems = 'center';
  placeholder.style.justifyContent = 'center';
  placeholder.style.borderRadius = '6px';
  placeholder.style.color = '#999';
  placeholder.style.fontSize = '12px';
  placeholder.textContent = message;
  container.replaceChildren(placeholder);
};

const createHeartbeatOverlayNode = () => {
  const svgNamespace = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNamespace, 'svg');
  svg.setAttribute('width', '60');
  svg.setAttribute('height', '60');
  svg.style.position = 'absolute';
  svg.style.top = '-30px';
  svg.style.left = '-30px';

  const circle = document.createElementNS(svgNamespace, 'circle');
  circle.setAttribute('cx', '30');
  circle.setAttribute('cy', '30');
  circle.setAttribute('r', '10');
  circle.setAttribute('fill', '#1890ff');
  circle.setAttribute('fill-opacity', '0.4');
  circle.setAttribute('stroke', '#1890ff');
  circle.setAttribute('stroke-width', '1');
  circle.setAttribute('stroke-opacity', '0.8');

  const radiusAnimation = document.createElementNS(svgNamespace, 'animate');
  radiusAnimation.setAttribute('attributeName', 'r');
  radiusAnimation.setAttribute('values', '10;30;30');
  radiusAnimation.setAttribute('dur', '2s');

  const fillAnimation = document.createElementNS(svgNamespace, 'animate');
  fillAnimation.setAttribute('attributeName', 'fill-opacity');
  fillAnimation.setAttribute('values', '0.4;0;0');
  fillAnimation.setAttribute('dur', '2s');

  const strokeAnimation = document.createElementNS(svgNamespace, 'animate');
  strokeAnimation.setAttribute('attributeName', 'stroke-opacity');
  strokeAnimation.setAttribute('values', '0.8;0;0');
  strokeAnimation.setAttribute('dur', '2s');

  circle.append(radiusAnimation, fillAnimation, strokeAnimation);
  svg.appendChild(circle);
  return svg;
};

// SimpleMapコンポーネント
const SimpleMap: React.FC<{ gnssSummary?: any; heartbeatTrigger?: number; device?: Device }> = ({ gnssSummary, heartbeatTrigger, device }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const centerPositionRef = useRef<any>(null);

  useEffect(() => {
    const initMap = () => {
      if (mapRef.current && window.google && window.google.maps) {
        // GNSSデータがある場合はその位置、ない場合は東京駅
        const center = gnssSummary?.latest_snapshot 
          ? { lat: gnssSummary.latest_snapshot.latitude, lng: gnssSummary.latest_snapshot.longitude }
          : { lat: 35.6812, lng: 139.7671 };
          
        const map = new window.google.maps.Map(mapRef.current, {
          zoom: 15,
          center: center,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          rotateControl: false,
          tiltControl: false,
        });
        
        mapInstanceRef.current = map;
        centerPositionRef.current = center;
        
        // GNSSデータがある場合はマーカーと精度範囲を表示
        if (gnssSummary?.latest_snapshot) {
          // バッチモードかどうかで色を変更
          const isRealtimeMode = device?.operation_mode === 'realtime';
          const strokeColor = isRealtimeMode ? '#1890ff' : '#999999';
          const fillColor = isRealtimeMode ? '#1890ff' : '#999999';
          
          // 精度範囲を表示する円
          new window.google.maps.Circle({
            strokeColor: strokeColor,
            strokeOpacity: 1.0,
            strokeWeight: 0.5,
            fillColor: fillColor,
            fillOpacity: 0.2,
            map: map,
            center: center,
            radius: gnssSummary.latest_snapshot.accuracy
          });
          
          // 中心点のマーカー
          const markerFillColor = isRealtimeMode ? '#1890ff' : '#999999';
          const circleIcon = {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="8" fill="${markerFillColor}" stroke="#fff" stroke-width="2"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(20, 20),
            anchor: new window.google.maps.Point(10, 10)
          };
          
          new window.google.maps.Marker({
            position: center,
            map: map,
            title: `現在位置 (精度: ${gnssSummary.latest_snapshot.accuracy}m)`,
            icon: circleIcon,
          });
        }
      }
    };

    // Google Maps APIが既に読み込まれている場合
    if (window.google && window.google.maps) {
      initMap();
      return;
    }

    // APIキーを取得
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
      if (mapRef.current) {
        renderMapPlaceholder(mapRef.current, 'Google Maps APIキーが設定されていません');
      }
      return;
    }

    // 既存のスクリプトをチェック
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // 既存スクリプトの読み込み完了を待つ
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          initMap();
        }
      }, 100);
      return;
    }

    // スクリプトを動的に読み込み
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`;
    script.async = true;
    script.onload = () => {
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          initMap();
        }
      }, 100);
    };
    document.head.appendChild(script);
  }, [gnssSummary, device?.operation_mode]);
  
  // ハートビートアニメーション
  useEffect(() => {
    if (heartbeatTrigger && mapInstanceRef.current && centerPositionRef.current && gnssSummary?.latest_snapshot) {
      const map = mapInstanceRef.current;
      const center = centerPositionRef.current;
      
      // 縮尺固定サイズの波紋アニメーション用のSVGオーバーレイ
      const overlay = new window.google.maps.OverlayView();
      
      overlay.onAdd = function() {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.pointerEvents = 'none';
        div.appendChild(createHeartbeatOverlayNode());
        
        this.getPanes().overlayMouseTarget.appendChild(div);
        this.div = div;
        
        // 2秒後に削除
        setTimeout(() => {
          if (this.div && this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
          this.setMap(null);
        }, 2000);
      };
      
      overlay.draw = function() {
        const projection = this.getProjection();
        const position = projection.fromLatLngToDivPixel(new window.google.maps.LatLng(center.lat, center.lng));
        
        if (this.div) {
          this.div.style.left = position.x + 'px';
          this.div.style.top = position.y + 'px';
        }
      };
      
      overlay.onRemove = function() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
        }
      };
      
      overlay.setMap(map);
    }
  }, [heartbeatTrigger, gnssSummary]);

  return (
    <div style={{ height: '280px', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
      <div 
        ref={mapRef}
        style={{ 
          width: '100%', 
          height: '100%',
          borderRadius: '6px'
        }} 
      />
    </div>
  );
};

interface Device {
  id: string;
  device_id: string;
  imei: string;
  name: string;
  device_type: string;
  tags?: string[];
  operation_mode: 'batch' | 'realtime';
  last_seen_at?: string;
  created_at: string;
  heartbeatTrigger?: number;
}

interface DeviceCurrentStatusProps {
  device: Device;
  batteryHistory: any[];
  networkHistory: any[];
  networkSummary?: any;
  gnssSummary?: any;
}

const DeviceCurrentStatus: React.FC<DeviceCurrentStatusProps> = ({ 
  device, 
  batteryHistory, 
  networkHistory,
  networkSummary,
  gnssSummary 
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  const getLatestBattery = () => {
    if (batteryHistory.length === 0) return null;
    return batteryHistory.sort((a, b) => 
      new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime()
    )[0];
  };

  const getLatestNetwork = () => {
    if (networkHistory.length === 0) return null;
    return networkHistory.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];
  };

  const latestBattery = getLatestBattery();
  const latestNetwork = getLatestNetwork();

  const getConnectionStatus = () => {
    if (device.operation_mode === 'realtime') {
      return <Tag color="green">リアルタイムモード</Tag>;
    }
    return <Tag color="default">バッチモード</Tag>;
  };

  const getBatteryStatus = () => {
    if (!latestBattery) return <Text type="secondary">データなし</Text>;
    
    const level = latestBattery.battery_level;
    const status = latestBattery.charging_status;
    const color = level > 20 ? '#52c41a' : level > 10 ? '#faad14' : '#ff4d4f';
    
    return (
      <Space>
        <div style={{ 
          width: 60, 
          height: 8, 
          backgroundColor: '#f0f0f0', 
          borderRadius: 4,
          overflow: 'hidden',
          display: 'inline-block'
        }}>
          <div style={{
            width: `${level}%`,
            height: '100%',
            backgroundColor: color,
          }} />
        </div>
        <Text>{level}%</Text>
        <Tag color={status === 'charging' ? 'green' : 'orange'}>
          {status === 'charging' ? '充電中' : '放電中'}
        </Tag>
      </Space>
    );
  };

  const getNetworkStatus = () => {
    // ネットワークサマリーがある場合はそれを使用
    if (networkSummary?.latest_snapshot) {
      const snapshot = networkSummary.latest_snapshot;
      return (
        <Space direction="vertical" size={6} style={{ width: '100%', marginTop: '8px' }}>
          {snapshot.status === 'offline' && (
            <div className={styles.networkInfoRow}>
              <Text style={{ fontSize: '12px', color: '#666' }}>状態:</Text>
              <Tag color="red" style={{ fontSize: '11px', padding: '1px 6px' }}>オフライン</Tag>
            </div>
          )}
          {snapshot.connection_type && (
            <div className={styles.networkInfoRow}>
              <Text style={{ fontSize: '12px', color: '#666' }}>接続種別:</Text>
              <Tag color={snapshot.connection_type === 'wifi' ? 'blue' : 'orange'} style={{ fontSize: '11px', padding: '1px 6px' }}>
                {snapshot.connection_type === 'wifi' ? 'Wi-Fi' : 'セルラー'}
              </Tag>
            </div>
          )}
          {snapshot.ip_address && (
            <div className={styles.networkInfoRow}>
              <Text style={{ fontSize: '12px', color: '#666' }}>IPアドレス:</Text>
              <Text style={{ fontSize: '12px', color: '#262626' }}>{snapshot.ip_address}</Text>
            </div>
          )}
          {snapshot.wifi_ssid && (
            <div className={styles.networkInfoRow}>
              <Text style={{ fontSize: '12px', color: '#666' }}>SSID:</Text>
              <Text style={{ fontSize: '12px', color: '#262626' }}>{snapshot.wifi_ssid}</Text>
            </div>
          )}
          {snapshot.wifi_signal_strength && (
            <div className={styles.networkInfoRow}>
              <Text style={{ fontSize: '12px', color: '#666' }}>信号強度:</Text>
              <Text style={{ fontSize: '12px', color: '#262626' }}>{snapshot.wifi_signal_strength} dBm</Text>
            </div>
          )}
          {snapshot.cellular_carrier && (
            <div className={styles.networkInfoRow}>
              <Text style={{ fontSize: '12px', color: '#666' }}>キャリア:</Text>
              <Text style={{ fontSize: '12px', color: '#262626' }}>{snapshot.cellular_carrier}</Text>
            </div>
          )}
          {snapshot.cellular_network_type && (
            <div className={styles.networkInfoRow}>
              <Text style={{ fontSize: '12px', color: '#666' }}>ネットワーク:</Text>
              <Text style={{ fontSize: '12px', color: '#262626' }}>{snapshot.cellular_network_type}</Text>
            </div>
          )}
          {snapshot.cellular_signal_strength && (
            <div className={styles.networkInfoRow}>
              <Text style={{ fontSize: '12px', color: '#666' }}>セルラー信号:</Text>
              <Text style={{ fontSize: '12px', color: '#262626' }}>{snapshot.cellular_signal_strength} dBm</Text>
            </div>
          )}
          <div style={{ marginTop: '8px' }}>
            <textarea
              readOnly
              value={JSON.stringify(networkSummary, null, 2)}
              style={{
                width: '100%',
                height: '120px',
                fontSize: '10px',
                fontFamily: 'monospace',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4px',
                backgroundColor: '#f5f5f5'
              }}
            />
          </div>
        </Space>
      );
    }
    
    // フォールバック: 既存のロジック
    if (!latestNetwork) return <Text type="secondary">データなし</Text>;
    
    return (
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        {latestNetwork.status === 'offline' && (
          <div className={styles.networkInfoRow}>
            <Text style={{ fontSize: '12px', color: '#666' }}>状態:</Text>
            <Tag color="red" style={{ fontSize: '11px', padding: '1px 6px' }}>オフライン</Tag>
          </div>
        )}
        {latestNetwork.connection_type && (
          <div className={styles.networkInfoRow}>
            <Text style={{ fontSize: '12px', color: '#666' }}>接続種別:</Text>
            <Tag color={latestNetwork.connection_type === 'wifi' ? 'blue' : 'orange'} style={{ fontSize: '11px', padding: '1px 6px' }}>
              {latestNetwork.connection_type === 'wifi' ? 'Wi-Fi' : 'セルラー'}
            </Tag>
          </div>
        )}
        {latestNetwork.wifi_ssid && (
          <div className={styles.networkInfoRow}>
            <Text style={{ fontSize: '12px', color: '#666' }}>SSID:</Text>
            <Text style={{ fontSize: '12px', color: '#262626' }}>{latestNetwork.wifi_ssid}</Text>
          </div>
        )}
      </Space>
    );
  };

  return (
    <Card title={<span style={{ fontSize: '12px' }}>現在の状態</span>} size="small">
      <Row gutter={16}>
        <Col span={12}>
          <Descriptions column={1} size="small" style={{ fontSize: '12px' }}>
        <Descriptions.Item 
          label={
            <Space>
              <SignalFilled />
              接続状態
            </Space>
          }
        >
          {getConnectionStatus()}
        </Descriptions.Item>
        
        <Descriptions.Item 
          label={
            <Space>
              <ClockCircleOutlined />
              最終同期
            </Space>
          }
        >
          {device.last_seen_at ? formatDate(device.last_seen_at) : '未接続'}
        </Descriptions.Item>
        
        <Descriptions.Item 
          label={
            <Space>
              <ThunderboltOutlined />
              バッテリー
            </Space>
          }
        >
          {getBatteryStatus()}
        </Descriptions.Item>
        
          </Descriptions>
          
          {/* ネットワーク情報を独立表示 */}
          <div style={{ marginTop: '16px' }}>
            <div>
              <Space>
                <WifiOutlined />
                <span style={{ fontWeight: 'bold' }}>ネットワーク</span>
              </Space>
              <div style={{ borderBottom: '1px solid #d9d9d9', marginTop: '4px' }}></div>
            </div>
            {getNetworkStatus()}
          </div>
          
          {/* 位置情報を独立表示 */}
          <div style={{ marginTop: '16px' }}>
            <div>
              <Space>
                <EnvironmentOutlined />
                <span style={{ fontWeight: 'bold' }}>位置情報</span>
              </Space>
              <div style={{ borderBottom: '1px solid #d9d9d9', marginTop: '4px' }}></div>
            </div>
            <div style={{ marginTop: '8px' }}>
              {gnssSummary?.latest_snapshot ? (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <div className={styles.networkInfoRow}>
                    <Text style={{ fontSize: '12px', color: '#666' }}>緯度:</Text>
                    <Text style={{ fontSize: '12px', color: '#262626' }}>{gnssSummary.latest_snapshot.latitude.toFixed(6)}</Text>
                  </div>
                  <div className={styles.networkInfoRow}>
                    <Text style={{ fontSize: '12px', color: '#666' }}>経度:</Text>
                    <Text style={{ fontSize: '12px', color: '#262626' }}>{gnssSummary.latest_snapshot.longitude.toFixed(6)}</Text>
                  </div>
                  <div className={styles.networkInfoRow}>
                    <Text style={{ fontSize: '12px', color: '#666' }}>精度:</Text>
                    <Text style={{ fontSize: '12px', color: '#262626' }}>{gnssSummary.latest_snapshot.accuracy}m</Text>
                  </div>
                  <div className={styles.networkInfoRow}>
                    <Text style={{ fontSize: '12px', color: '#666' }}>衛星数:</Text>
                    <Text style={{ fontSize: '12px', color: '#262626' }}>{gnssSummary.latest_snapshot.satellite_count}個</Text>
                  </div>
                </Space>
              ) : (
                <Text type="secondary">データなし</Text>
              )}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <SimpleMap gnssSummary={gnssSummary} heartbeatTrigger={device.heartbeatTrigger} device={device} />
        </Col>
      </Row>
    </Card>
  );
};

export default DeviceCurrentStatus;
